import "./App.css";
import "../custom.scss";
import React from "react";
import { Switch, Route, Redirect } from "react-router-dom";
import { LatestResearch } from "pages/latestResearch/LatestResearch";
import { Search, GotoSearch } from "pages/search/SearchPage";
import { LogIn } from "pages/login/LogIn";
import { ForgottenPassword } from "pages/login/ForgottenPassword";
import { ForgottenPwdSent } from "pages/login/ForgottenPwdSent";
import { LoginSupport } from "pages/login/LoginSupport";
import { NotFound } from "pages/login/NotFound";
import { SearchResults } from "pages/searchResults/SearchResults";
import { CoveragePage } from "pages/coverage/Coverage";
import { AdvertProvider } from "pages/ads/adverts/AdvertProvider";
import { AdvancedSearch } from "pages/advancedSearch/AdvancedSearch";
import { MostPopular } from "pages/mostPopular/MostPopular";
import { LogOut } from "pages/login/Logout";
import { AnalystProfile } from "pages/analystProfile/AnalystProfile";
import { AnalystDirectory } from "pages/analystDirectory/AnalystDirectory";
import { LeaveSiteWarning } from "pages/ads/adverts/LeaveSiteWarning";
import { Faq } from "pages/faq/Faq";
import { Disclosures } from "pages/disclosures/Disclosures";
import { AdvertPreview } from "pages/ads/adverts/preview/AdvertPreview";
import { ToastNotificationsService } from "pages/readingList/notifications/ToastNotificationsService";
import { ReadingList } from "pages/readingList/ReadingList";
import { LandingPage } from "pages/landingPage/LandingPage";
import { FaqPreview } from "pages/faq/FaqPreview";
import { AllAdverts } from "pages/ads/adverts/preview/AllAdverts";
import { LazyEconomicData } from "pages/economicData/LazyEconomicData";
import { SessionExpired } from "pages/login/SessionExpired";
import { DisclosuresStatement } from "pages/disclosures/parts/DisclosuresStatement";
import { Header } from "pages/redesign/header/Header";

import Redesign from "pages/redesign/Redesign";
import { useIsRedesign } from "pages/redesign/preferences/redesignUtils";
import { MagicLink } from "pages/login/MagicLink";
import { MagicLinkVerify } from "pages/login/MagicLinkVerify";
import { VideoPlayer } from "pages/videoplayer/VideoPlayer";
import { Events } from "pages/events/Events";
import NewDesignModal from "lib/NewDesignModal/NewDesignModal";
import FeedbackModal from "lib/FeedbackModal/FeedbackModal";
import { Disclaimers } from "./disclaimers/Disclaimers";
import { features } from "./features";
import { ScreenHeight } from "../lib/ScreenHeight";
import { PreferencesProvider } from "../pages/preferences/provider/PreferencesProvider";
import { ChangePassword } from "../pages/login/ChangePassword";
import { MyResearch, GotoMyResearch } from "../pages/myResearch/MyResearch";
import { Preferences } from "../pages/preferences/Preferences";
import { ScrollToTop } from "../lib/ScrollToTop";
import { TermsOfService } from "./disclaimers/TermsOfService";
import { Footer } from "./footer/Footer";
import { ScrollToTopButton } from "./footer/parts/ScrollToTopButton";

function App() {
  return (
    <PreferencesProvider>
      <Header />
      <ScreenHeight>
        <TermsOfService />
        <AdvertProvider />
        <ScrollToTop />
        <Switch>
          {/* Public paths */}
          <Route path="/public/login" component={LogIn} />
          <Route path="/public/forgotten-password/" component={ForgottenPassword} />
          <Route path="/public/forgotten-password-sent/" component={ForgottenPwdSent} />
          <Route path="/public/magic-link/" component={MagicLink} />
          <Route path="/public/magic-link-verify/" component={MagicLinkVerify} />
          <Route path="/public/login-support/" component={LoginSupport} />
          <Route path="/public/change-password/" component={ChangePassword} />
          <Route path="/public/session_expired" component={SessionExpired} />
          <Route path="/public/Disclaimers/:disclaimer" component={Disclaimers} />
          <Route path="/public/Disclosures/Statements" component={DisclosuresStatement} />
          <Route path="/public/Disclosures" component={Disclosures} />
          <Route path="/public/videoplayer/" component={VideoPlayer} />
          <Route path="/public/Analyst_Directory/" component={AnalystDirectory} />
          <Route path="/public/brochures" component={LandingPage} />

          {/* Directus preview */}
          {features.preview.faq && <Route path="/FAQ/preview/:id" component={FaqPreview} />}
          {features.preview.disclaimer && <Route path="/Disclaimers/preview/:disclaimer" component={Disclaimers} />}
          {features.preview.banners && <Route path="/advert/preview/all" component={AllAdverts} />}
          {features.preview.banners && <Route path="/advert/preview/:id" component={AdvertPreview} />}
          {features.preview.banners && <Route path="/advert2/preview/:id" component={AdvertPreview} />}
          {features.preview.landingPages && <Route path="/Landing_Page/preview/:topic" component={LandingPage} />}

          {RedesignRedirectionRules()}

          <Route path="/" exact component={LatestResearch} />
          <Route path="/latest-research/:assetClass?/:region?/:product?" component={LatestResearch} />
          <Route path="/Search" component={Search} />
          <Route path="/Search_Results" component={SearchResults} />
          <Route path="/Preferences" component={Preferences} />
          <Route path="/Most_Popular" component={MostPopular} />
          <Route path="/My_Research" component={MyResearch} />
          <Route path="/My_Reading_List" component={ReadingList} />
          <Route path="/Advanced_Search" component={AdvancedSearch} />
          <Route path="/FAQ" component={Faq} />
          <Route path="/Sign_Out" component={LogOut} />
          <Route path="/Goto_Search" component={GotoSearch} />
          <Route path="/Goto_My_Research" component={GotoMyResearch} />
          <Route path="/Equity_Coverage" component={CoveragePage} />
          <Route path="/Analyst_Directory" component={AnalystDirectory} />
          <Route path="/Analyst" component={AnalystProfile} />
          <Route path="/GotoWarning" component={LeaveSiteWarning} />
          <Route path="/Disclaimers/:disclaimer" component={Disclaimers} />

          {/* feature-flag routes */}
          {features.disclosures && <Route path="/Disclosures/Statements" component={DisclosuresStatement} />}
          {features.disclosures && <Route path="/Disclosures" component={Disclosures} />}
          {features.economicForecast && <Route path="/Economic_Data/" component={LazyEconomicData} />}
          {features.bannerLandingPages && <Route path="/Topics/:topic" component={LandingPage} />}

          {/* extension routes */}
          {features.events && <Route path="/Events" component={Events} />}

          {/* legacy URLs */}
          {features.economicForecast && <Route path="/Topics/Economic_Forecast" component={LazyEconomicData} />}
          {features.economicForecast && <Route path="/Economic_Forecast" component={LazyEconomicData} />}
          <Route component={NotFound} />
        </Switch>
      </ScreenHeight>
      <NewDesignModal />
      <FeedbackModal />
      <ToastNotificationsService />
      <ScrollToTopButton />
      <Footer />
    </PreferencesProvider>
  );
}

export default App;

const RedesignRedirectionRules = () =>
  useIsRedesign()
    ? [
        <Route key={0} path="/latest-research/Equity" component={() => <Redirect to="/Equity" />} />,
        <Route key={1} path="/latest-research/Economics" component={() => <Redirect to="/Economics" />} />,
        <Route key={2} path="/latest-research/FX" component={() => <Redirect to="/FX" />} />,
        <Route key={3} path="/latest-research/Quants" component={() => <Redirect to="/Quants" />} />,
        <Route key={4} path="/latest-research/Quants Focus" component={() => <Redirect to="/Quants" />} />,
        <Route key={5} path="/latest-research/Rates" component={() => <Redirect to="/Rates" />} />,
        <Route key={6} path="/latest-research/Rates Focus" component={() => <Redirect to="/Rates" />} />,
        <Route key={7} path="/latest-research/SP__Credit" component={() => <Redirect to="/SP__Credit" />} />,
        <Route key={8} path="/latest-research/SP__Credit Focus" component={() => <Redirect to="/SP__Credit" />} />,
        <Route key={9} path="/latest-research/ESG" component={() => <Redirect to="/Home?reportTypes.en=ESG" />} />,
        <Route key={10} path="/latest-research/" component={() => <Redirect to="/Home" />} />,
        <Route key={11} path="/" exact component={() => <Redirect to="/Home" />} />,
        <Route key={12} path="/Credit" component={() => <Redirect to="/SP__Credit" />} />,

        <Route key={13} path={redesignPaths} component={Redesign} />,
      ]
    : [<Route key={14} path={redesignPaths} exact component={() => <Redirect to="/latest-research" />} />];

const redesignPaths = ["/Home", "/Economics", "/Equity", "/FX", "/Rates", "/SP__Credit", "/Macro Strategy", "/Quants"];
